import styles from "./page.module.css";

import ManageSensorsGrid from "./components/manage-sensors.grid";
import { observer, inject } from "mobx-react";
import { SensorDataStore } from "./store/store";
import Header from "./components/header/header";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { TransitionStore } from "../../store";
import { useNavigate } from "react-router-dom";
import LocationSelectorStore from "../../store/location-selector-store";
import { Pagination } from "@mui/material";

interface ISensorProps {
    sensorDataStore: SensorDataStore;
    transitionStore: TransitionStore;
    locationSelectorStore: LocationSelectorStore;
}

function Sensors(props: ISensorProps) {
    const { t, ready: readyCameraGrid } = useTranslation("sensor-grid");
    const { ready: readyFilter } = useTranslation("filter-camera");
    const { ready: readySearch } = useTranslation("search-sensor");
    const { ready: readyCommon } = useTranslation("common");
    const { sensorDataStore, transitionStore } = props;
    const {
        isDataLoading,
        isTranslationLoading,
        isSaveLoading,
        isDeleteLoading,
    } = sensorDataStore;

    const languagesLoaded =
        readyCameraGrid && readyFilter && readySearch && readyCommon;

    const data = sensorDataStore.sensors;
    const ref = useRef<any>(null);

    let navigate = useNavigate();

    useEffect(() => {
        return () => {
            sensorDataStore.resetSelectedData();
            sensorDataStore.closeExpanded();
            sensorDataStore.setSensors([]);
            sensorDataStore.setPageNumber(0);
            sensorDataStore.setSearchValue("");
            sensorDataStore.setSearchDisplay("");
            transitionStore.isPromptShown = false;
        };
    }, []);

    useEffect(() => {
        if (ref.current) ref.current.scrollTop = 0;
    }, [data]);

    useEffect(() => {
        if (!languagesLoaded) {
            sensorDataStore.setIsTranslationLoading(true);
        } else {
            sensorDataStore.setIsTranslationLoading(false);
        }

        if (
            isDataLoading ||
            isTranslationLoading ||
            isSaveLoading ||
            isDeleteLoading
        ) {
            transitionStore.setLoading(true);
        } else {
            transitionStore.setLoading(false);
        }
    });

    useEffect(() => {
        transitionStore.isPromptShown = sensorDataStore.hasSelectedSensor;
    }, [sensorDataStore.hasSelectedSensor]);

    const showPagination = () => {
        return sensorDataStore.pageNumber > 0 && sensorDataStore.pageCount > 1;
    };

    const render = () => {
        return (
            <>
                {languagesLoaded && (
                    <>
                        <Header />
                        <div
                            id="grid-container"
                            className={styles["manage-sensors-grid-container"]}
                        >
                            <div
                                ref={ref}
                                className={styles["overflow-content"]}
                            >
                                <ManageSensorsGrid />
                            </div>
                        </div>
                        <div className={styles["pagination-container"]}>
                            {showPagination() && (
                                <>
                                    <div>
                                        {t("PageCountLabel", {
                                            currentPage:
                                                sensorDataStore.pageNumber,
                                            totalPages:
                                                sensorDataStore.pageCount,
                                            items: sensorDataStore.totalItemCount,
                                        })}
                                    </div>

                                    <Pagination
                                        disabled={
                                            isDataLoading ||
                                            isTranslationLoading ||
                                            isSaveLoading ||
                                            isDeleteLoading
                                        }
                                        count={sensorDataStore.pageCount}
                                        page={sensorDataStore.pageNumber}
                                        onChange={(_event, pageNumber) => {
                                            navigate(
                                                `/manage-sensors?page=${pageNumber}`
                                            );
                                            sensorDataStore.setPageNumber(
                                                pageNumber
                                            );
                                            sensorDataStore.setNewRequestPage(
                                                pageNumber
                                            );
                                        }}
                                    />
                                </>
                            )}
                        </div>
                    </>
                )}
            </>
        );
    };

    return render();
}

export default inject(
    "sensorDataStore",
    "transitionStore",
    "locationSelectorStore"
)(observer(Sensors));
